import { formatSydneyDateTime } from '../utils/timezone';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { formatDateTime, getRelativeTime, severityColors, statusColors } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { LicenceComplianceWidget } from '../components/LicenceComplianceWidget';
import WeatherForecastWidget from '../components/WeatherForecastWidget';
import { 
  AlertTriangle, 
  Shield, 
  Users, 
  MapPin, 
  Clock, 
  RefreshCw,
  TrendingUp,
  FileWarning,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Activity,
  FileCheck,
  Car,
  Heart,
  Trash2
} from 'lucide-react';

export default function DashboardPage() {
  const { api, user, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentIncidents, setRecentIncidents] = useState([]);
  const [patrolExceptions, setPatrolExceptions] = useState([]);
  const [sitesSummary, setSitesSummary] = useState([]);
  // Q_SITE_ACTIVITY_PAGINATION_V1
  const [siteSearch, setSiteSearch] = useState('');
  const [siteClient, setSiteClient] = useState('');
  const [sitePage, setSitePage] = useState(1);

  const [complianceSummary, setComplianceSummary] = useState(null);
  const [complianceDashboard, setComplianceDashboard] = useState(null);
  const [attendanceFlags, setAttendanceFlags] = useState({ flags: [], today_count: 0 });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsRes, incidentsRes, sitesRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/dashboard/recent-incidents?limit=5'),
        api.get('/dashboard/sites-summary')
      ]);
      
      setStats(statsRes.data);
      setRecentIncidents(incidentsRes.data);
      setSitesSummary(sitesRes.data);
      setLastUpdated(new Date());
      
      // Fetch patrol exceptions and compliance summary for admins
      if (user?.role === 'super_admin' || user?.role === 'operations_admin') {
        const [exceptionsRes, complianceRes, complianceDashboardRes, flagsRes] = await Promise.all([
          api.get('/dashboard/patrol-exceptions?limit=5'),
          api.get('/licences/summary'),
          api.get('/users/compliance/dashboard'),
          api.get('/attendance/flags?resolved=false').catch(() => ({ data: { flags: [], today_count: 0 } }))
        ]);
        setPatrolExceptions(exceptionsRes.data);
        setComplianceSummary(complianceRes.data);
        setComplianceDashboard(complianceDashboardRes.data);
        setAttendanceFlags(flagsRes.data || { flags: [], today_count: 0 });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [api, user]);

  useEffect(() => {
    fetchDashboardData();
    
    // Poll every 30 seconds for real-time updates
    const interval = setInterval(fetchDashboardData, 30000);
    const clock = setInterval(() => setLastUpdated(new Date()), 1000);
    return () => {
      clearInterval(interval);
      clearInterval(clock);
    };
  }, [fetchDashboardData]);


  const siteRows = Array.isArray(sitesSummary) ? sitesSummary : [];
  const siteClients = [...new Set(
    siteRows.map((site) => site.client_name).filter(Boolean)
  )].sort((a, b) => String(a).localeCompare(String(b)));

  const siteQuery = siteSearch.trim().toLowerCase();
  const filteredSiteRows = siteRows.filter((site) => {
    const matchesClient = !siteClient || site.client_name === siteClient;
    const searchable = [
      site.name, site.code, site.client_name, site.region
    ].filter(Boolean).join(' ').toLowerCase();

    return matchesClient && (!siteQuery || searchable.includes(siteQuery));
  });

  const sitePageSize = 10;
  const sitePageCount = Math.max(
    1, Math.ceil(filteredSiteRows.length / sitePageSize)
  );
  const currentSitePage = Math.min(sitePage, sitePageCount);
  const siteStart = (currentSitePage - 1) * sitePageSize;
  const visibleSiteRows = filteredSiteRows.slice(
    siteStart, siteStart + sitePageSize
  );

  const handleRefresh = () => {
    setLoading(true);
    fetchDashboardData();
  };

  const handleDeleteFlag = async (flagId) => {
    try {
      await api.delete(`/attendance/flags/${flagId}`);
      toast.success('Flag deleted');
      // Refresh the flags from correct endpoint
      const flagsRes = await api.get('/attendance/flags?resolved=false');
      setAttendanceFlags(flagsRes.data || { flags: [], today_count: 0 });
    } catch (error) {
      console.error('Error deleting flag:', error);
      toast.error('Failed to delete flag');
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="rx-dashboard space-y-6" data-testid="dashboard-page">

      <div className="mb-6">
        <WeatherForecastWidget />
      </div>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="rx-dashboard-title text-2xl font-rajdhani font-bold tracking-wide">
            EXECUTIVE OVERVIEW
          </h1>
          <p className="text-sm text-zinc-500 font-mono">
            Last updated: {formatDateTime(lastUpdated)}
          </p>
        </div>
        <Button
          onClick={handleRefresh}
          variant="outline"
          size="sm"
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          data-testid="dashboard-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          REFRESH
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {/* Critical Incidents */}
        <Card className="rx-dashboard-card bg-[#18181b] border-[#3f3f46] col-span-1">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <AlertTriangle className="w-5 h-5 text-red-400" strokeWidth={1.5} />
              <span className={`text-2xl font-mono font-bold ${stats?.critical_incidents > 0 ? 'text-red-400' : 'text-zinc-400'}`}>
                {stats?.critical_incidents || 0}
              </span>
            </div>
            <p className="text-xs text-zinc-500 mt-2 font-rajdhani uppercase tracking-wide">Critical</p>
          </CardContent>
        </Card>

        {/* Pending Review */}
        <Card className="rx-dashboard-card bg-[#18181b] border-[#3f3f46] col-span-1">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <FileWarning className="w-5 h-5 text-yellow-400" strokeWidth={1.5} />
              <span className={`text-2xl font-mono font-bold ${stats?.pending_review_incidents > 0 ? 'text-yellow-400' : 'text-zinc-400'}`}>
                {stats?.pending_review_incidents || 0}
              </span>
            </div>
            <p className="text-xs text-zinc-500 mt-2 font-rajdhani uppercase tracking-wide">Pending Review</p>
          </CardContent>
        </Card>

        {/* Today's Incidents */}
        <Card className="rx-dashboard-card bg-[#18181b] border-[#3f3f46] col-span-1">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Activity className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
              <span className="text-2xl font-mono font-bold text-zinc-300">
                {stats?.incidents_today || 0}
              </span>
            </div>
            <p className="text-xs text-zinc-500 mt-2 font-rajdhani uppercase tracking-wide">Incidents Today</p>
          </CardContent>
        </Card>

        {/* Officers On Duty */}
        <Card className="rx-dashboard-card bg-[#18181b] border-[#3f3f46] col-span-1">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Users className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
              <span className="text-2xl font-mono font-bold text-emerald-400">
                {stats?.officers_on_duty || 0}
              </span>
            </div>
            <p className="text-xs text-zinc-500 mt-2 font-rajdhani uppercase tracking-wide">On Duty</p>
          </CardContent>
        </Card>

        {/* Patrol Compliance */}
        <Card className="rx-dashboard-card bg-[#18181b] border-[#3f3f46] col-span-1">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
              <span className={`text-2xl font-mono font-bold ${stats?.patrol_compliance_rate >= 90 ? 'text-emerald-400' : 'text-yellow-400'}`}>
                {stats?.patrol_compliance_rate?.toFixed(0) || 0}%
              </span>
            </div>
            <p className="text-xs text-zinc-500 mt-2 font-rajdhani uppercase tracking-wide">Patrol Compliance</p>
          </CardContent>
        </Card>

        {/* Open Gates */}
        <Card className="rx-dashboard-card bg-[#18181b] border-[#3f3f46] col-span-1">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <AlertCircle className="w-5 h-5 text-orange-400" strokeWidth={1.5} />
              <span className={`text-2xl font-mono font-bold ${stats?.open_gates > 0 ? 'text-orange-400' : 'text-zinc-400'}`}>
                {stats?.open_gates || 0}
              </span>
            </div>
            <p className="text-xs text-zinc-500 mt-2 font-rajdhani uppercase tracking-wide">Open Gates</p>
          </CardContent>
        </Card>
      </div>

      {/* Compliance Summary (Admin only) */}
      {isAdmin && complianceSummary && (
        <Card className="rx-dashboard-card bg-[#18181b] border-[#3f3f46]">
          <CardHeader className="rx-compliance-header border-b border-[#3f3f46] bg-[#18181b]/50 py-3 px-4">
            <CardTitle className="text-sm font-rajdhani font-semibold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
              <FileCheck className="w-4 h-4 text-orange-400" />
              LICENCE COMPLIANCE
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {/* Expired */}
              <div className="p-3 bg-red-500/10 rounded-sm border border-red-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="w-4 h-4 text-red-400" />
                  <span className="text-xs font-rajdhani text-red-400 uppercase">Expired</span>
                </div>
                <span className={`text-2xl font-mono font-bold ${complianceSummary.expired?.count > 0 ? 'text-red-400' : 'text-zinc-500'}`}>
                  {complianceSummary.expired?.count || 0}
                </span>
              </div>
              
              {/* Expiring 7 days */}
              <div className="p-3 bg-red-500/10 rounded-sm border border-red-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="text-xs font-rajdhani text-red-400 uppercase">≤7 Days</span>
                </div>
                <span className={`text-2xl font-mono font-bold ${complianceSummary.expiring_7_days?.count > 0 ? 'text-red-400' : 'text-zinc-500'}`}>
                  {complianceSummary.expiring_7_days?.count || 0}
                </span>
              </div>
              
              {/* Expiring 14 days */}
              <div className="p-3 bg-yellow-500/10 rounded-sm border border-yellow-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-400" />
                  <span className="text-xs font-rajdhani text-yellow-400 uppercase">≤14 Days</span>
                </div>
                <span className={`text-2xl font-mono font-bold ${complianceSummary.expiring_14_days?.count > 0 ? 'text-yellow-400' : 'text-zinc-500'}`}>
                  {complianceSummary.expiring_14_days?.count || 0}
                </span>
              </div>
              
              {/* Expiring 30 days */}
              <div className="p-3 bg-orange-500/10 rounded-sm border border-orange-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-orange-400" />
                  <span className="text-xs font-rajdhani text-orange-400 uppercase">≤30 Days</span>
                </div>
                <span className={`text-2xl font-mono font-bold ${complianceSummary.expiring_30_days?.count > 0 ? 'text-orange-400' : 'text-zinc-500'}`}>
                  {complianceSummary.expiring_30_days?.count || 0}
                </span>
              </div>
              
              {/* By Type Summary */}
              <div className="p-3 bg-[#27272a] rounded-sm border border-[#3f3f46]">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-blue-400" />
                  <span className="text-xs font-rajdhani text-zinc-400 uppercase">By Type</span>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1 text-zinc-500">
                      <Car className="w-3 h-3" /> Drivers
                    </span>
                    <span className={`font-mono ${complianceSummary.by_type?.drivers_licence?.expired > 0 || complianceSummary.by_type?.drivers_licence?.expiring > 0 ? 'text-yellow-400' : 'text-zinc-400'}`}>
                      {(complianceSummary.by_type?.drivers_licence?.expired || 0) + (complianceSummary.by_type?.drivers_licence?.expiring || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1 text-zinc-500">
                      <Shield className="w-3 h-3" /> Security
                    </span>
                    <span className={`font-mono ${complianceSummary.by_type?.security_licence?.expired > 0 || complianceSummary.by_type?.security_licence?.expiring > 0 ? 'text-yellow-400' : 'text-zinc-400'}`}>
                      {(complianceSummary.by_type?.security_licence?.expired || 0) + (complianceSummary.by_type?.security_licence?.expiring || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1 text-zinc-500">
                      <Heart className="w-3 h-3" /> First Aid
                    </span>
                    <span className={`font-mono ${complianceSummary.by_type?.first_aid?.expired > 0 || complianceSummary.by_type?.first_aid?.expiring > 0 ? 'text-yellow-400' : 'text-zinc-400'}`}>
                      {(complianceSummary.by_type?.first_aid?.expired || 0) + (complianceSummary.by_type?.first_aid?.expiring || 0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Staff Compliance Details (Admin only) */}
      {isAdmin && complianceDashboard && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Expired Staff */}
          <Card className="rx-dashboard-card bg-[#18181b] border-[#3f3f46]">
            <CardHeader className="border-b border-[#3f3f46] bg-red-500/5 py-3 px-4">
              <CardTitle className="text-sm font-rajdhani font-semibold text-red-400 uppercase tracking-wider flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  EXPIRED LICENCES
                </span>
                <Badge variant="destructive" className="text-xs">
                  {complianceDashboard.summary?.expired_count || 0}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {complianceDashboard.expired_staff?.length > 0 ? (
                <div className="divide-y divide-[#3f3f46]">
                  {complianceDashboard.expired_staff.map((staff, idx) => (
                    <div key={idx} className="p-3 hover:bg-red-500/5">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-zinc-200 font-medium">{staff.name}</p>
                          {staff.employee_id && (
                            <p className="text-xs text-zinc-500">ID: {staff.employee_id}</p>
                          )}
                        </div>
                        <div className="text-right">
                          {staff.issues?.map((issue, i) => (
                            <Badge 
                              key={i} 
                              variant="destructive" 
                              className="text-[10px] ml-1 mb-1"
                            >
                              {issue.type}: {issue.message}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center text-zinc-500 text-sm">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500/50" />
                  No expired licences
                </div>
              )}
            </CardContent>
          </Card>

          {/* Expiring Soon Staff */}
          <Card className="rx-dashboard-card bg-[#18181b] border-[#3f3f46]">
            <CardHeader className="border-b border-[#3f3f46] bg-yellow-500/5 py-3 px-4">
              <CardTitle className="text-sm font-rajdhani font-semibold text-yellow-400 uppercase tracking-wider flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  EXPIRING SOON (30 DAYS)
                </span>
                <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">
                  {complianceDashboard.summary?.expiring_soon_count || 0}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {complianceDashboard.expiring_staff?.length > 0 ? (
                <div className="divide-y divide-[#3f3f46]">
                  {complianceDashboard.expiring_staff.map((staff, idx) => (
                    <div key={idx} className="p-3 hover:bg-yellow-500/5">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-zinc-200 font-medium">{staff.name}</p>
                          {staff.employee_id && (
                            <p className="text-xs text-zinc-500">ID: {staff.employee_id}</p>
                          )}
                        </div>
                        <div className="text-right">
                          {staff.issues?.map((issue, i) => (
                            <Badge 
                              key={i} 
                              className="bg-yellow-500/20 text-yellow-400 text-[10px] ml-1 mb-1"
                            >
                              {issue.type}: {issue.days}d
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center text-zinc-500 text-sm">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500/50" />
                  No licences expiring soon
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* NSW SLED Licence Compliance Widget (Admin only) */}
      {isAdmin && (
        <LicenceComplianceWidget />
      )}

      {/* Attendance Check-In Flags (Admin only) */}
      {isAdmin && attendanceFlags.flags?.length > 0 && (
        <Card className="bg-[#18181b] border-[#3f3f46] border-l-4 border-l-orange-500">
          <CardHeader className="border-b border-[#3f3f46] bg-orange-500/5 py-3 px-4">
            <CardTitle className="text-sm font-rajdhani font-semibold text-orange-400 uppercase tracking-wider flex items-center justify-between">
              <span className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                CHECK-IN FLAGS
              </span>
              <Badge className="bg-orange-500/20 text-orange-400 text-xs">
                {attendanceFlags.today_count} today
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-[#3f3f46]">
              {attendanceFlags.flags.slice(0, 5).map((flag, idx) => (
                <div key={flag.id || idx} className="p-3 hover:bg-orange-500/5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`text-[10px] px-1.5 py-0 ${
                          flag.severity === 'high' || flag.severity === 'critical' 
                            ? 'bg-red-500/20 text-red-400 border-red-500/30' 
                            : flag.severity === 'medium'
                            ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                            : 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'
                        } border`}>
                          {flag.severity?.toUpperCase()}
                        </Badge>
                        <span className="text-xs text-zinc-500 font-mono">
                          {flag.flag_type?.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-300 truncate">{flag.description}</p>
                      <p className="text-xs text-zinc-500 mt-1">
                        {flag.created_at ? formatSydneyDateTime(flag.created_at, false) : 'Unknown'}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0"
                      onClick={() => handleDeleteFlag(flag.id)}
                      data-testid={`delete-flag-${flag.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            {attendanceFlags.flags.length > 5 && (
              <div className="p-2 text-center border-t border-[#3f3f46]">
                <span className="text-xs text-zinc-500">
                  +{attendanceFlags.flags.length - 5} more flags
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Incidents */}
        <Card className="bg-[#18181b] border-[#3f3f46] lg:col-span-2">
          <CardHeader className="rx-compliance-header border-b border-[#3f3f46] bg-[#18181b]/50 py-3 px-4">
            <CardTitle className="text-sm font-rajdhani font-semibold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
              <Shield className="w-4 h-4 text-blue-400" />
              RECENT INCIDENTS
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {recentIncidents.length === 0 ? (
              <div className="p-8 text-center text-zinc-500">
                <Shield className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No recent incidents</p>
              </div>
            ) : (
              <div className="divide-y divide-[#3f3f46]">
                {recentIncidents.map((incident) => (
                  <div 
                    key={incident.id} 
                    className="p-4 hover:bg-[#27272a]/50 transition-colors cursor-pointer"
                    data-testid={`incident-row-${incident.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono text-blue-400">{incident.ir_number}</span>
                          <Badge className={`${severityColors[incident.severity]} text-xs px-1.5 py-0 rounded-sm border`}>
                            {incident.severity.toUpperCase()}
                          </Badge>
                          <Badge className={`${statusColors[incident.status]} text-xs px-1.5 py-0 rounded-sm border`}>
                            {incident.status.replace(/_/g, ' ').toUpperCase()}
                          </Badge>
                        </div>
                        <p className="text-sm text-zinc-200 font-inter truncate">{incident.title}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-zinc-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {incident.site_name}
                          </span>
                          <span className="text-xs text-zinc-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {getRelativeTime(incident.occurred_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Attendance & Exceptions */}
        <Card className="rx-dashboard-card bg-[#18181b] border-[#3f3f46]">
          <CardHeader className="rx-compliance-header border-b border-[#3f3f46] bg-[#18181b]/50 py-3 px-4">
            <CardTitle className="text-sm font-rajdhani font-semibold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
              <Clock className="w-4 h-4 text-yellow-400" />
              ATTENDANCE ALERTS
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between p-3 bg-[#27272a] rounded-sm border border-[#3f3f46]">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-sm bg-yellow-500/20 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-yellow-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-zinc-300">Late Arrivals</p>
                  <p className="text-xs text-zinc-500">Today</p>
                </div>
              </div>
              <span className={`text-xl font-mono font-bold ${stats?.late_arrivals_today > 0 ? 'text-yellow-400' : 'text-zinc-400'}`}>
                {stats?.late_arrivals_today || 0}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-[#27272a] rounded-sm border border-[#3f3f46]">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-sm bg-red-500/20 flex items-center justify-center">
                  <XCircle className="w-4 h-4 text-red-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-zinc-300">No Shows</p>
                  <p className="text-xs text-zinc-500">Today</p>
                </div>
              </div>
              <span className={`text-xl font-mono font-bold ${stats?.no_shows_today > 0 ? 'text-red-400' : 'text-zinc-400'}`}>
                {stats?.no_shows_today || 0}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-[#27272a] rounded-sm border border-[#3f3f46]">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-sm bg-blue-500/20 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-zinc-300">Patrols Today</p>
                  <p className="text-xs text-zinc-500">{stats?.completed_patrols || 0} completed</p>
                </div>
              </div>
              <span className="text-xl font-mono font-bold text-blue-400">
                {stats?.total_patrols_today || 0}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-[#27272a] rounded-sm border border-[#3f3f46]">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-sm bg-orange-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-orange-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-zinc-300">Expiring Licenses</p>
                  <p className="text-xs text-zinc-500">30 days</p>
                </div>
              </div>
              <span className={`text-xl font-mono font-bold ${stats?.expiring_licenses_30_days > 0 ? 'text-orange-400' : 'text-zinc-400'}`}>
                {stats?.expiring_licenses_30_days || 0}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>


      {/* Site Activity Today */}
      <Card className="rx-dashboard-card bg-[#18181b] border-[#3f3f46]">
        <CardHeader className="rx-compliance-header border-b border-[#3f3f46] bg-[#18181b]/50 py-3 px-4">
          <CardTitle className="text-sm font-rajdhani font-semibold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
            <MapPin className="w-4 h-4 text-emerald-400" />
            Site Activity Today
          </CardTitle>
        </CardHeader>

        <CardContent className="p-0">
          <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-[#3f3f46]">
            <input
              type="search"
              aria-label="Search site activity"
              placeholder="Search site, code or region..."
              value={siteSearch}
              onChange={(event) => {
                setSiteSearch(event.target.value);
                setSitePage(1);
              }}
              className="w-full sm:flex-1 min-w-0 rounded-md border border-zinc-600 bg-[#27272a] px-3 py-2 text-sm text-zinc-200 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <select
              aria-label="Filter site activity by client"
              value={siteClient}
              onChange={(event) => {
                setSiteClient(event.target.value);
                setSitePage(1);
              }}
              className="w-full sm:w-64 rounded-md border border-zinc-600 bg-[#27272a] px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All clients</option>
              {siteClients.map((client) => (
                <option key={client} value={client}>{client}</option>
              ))}
            </select>
          </div>

          <div
            className="overflow-auto"
            style={{ maxHeight: '420px' }}
            tabIndex={0}
            role="region"
            aria-label="Site activity table"
          >
            <table className="w-full text-sm" style={{ minWidth: '720px' }}>
              <thead className="sticky top-0 z-10 bg-[#27272a]">
                <tr className="border-b border-[#3f3f46]">
                  <th scope="col" className="text-left p-3 text-xs text-zinc-400 uppercase">Site</th>
                  <th scope="col" className="text-left p-3 text-xs text-zinc-400 uppercase">Client</th>
                  <th scope="col" className="text-center p-3 text-xs text-zinc-400 uppercase">Region</th>
                  <th scope="col" className="text-center p-3 text-xs text-zinc-400 uppercase">Officers</th>
                  <th scope="col" className="text-center p-3 text-xs text-zinc-400 uppercase">Patrols</th>
                  <th scope="col" className="text-center p-3 text-xs text-zinc-400 uppercase">Incidents</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-[#3f3f46]">
                {visibleSiteRows.map((site) => (
                  <tr key={site.id} className="hover:bg-[#27272a]/50 transition-colors">
                    <td className="p-3">
                      <span className="text-sm text-zinc-200">{site.name}</span>
                      <span className="text-xs text-zinc-500 ml-2 font-mono">{site.code}</span>
                    </td>
                    <td className="p-3 text-sm text-zinc-400">{site.client_name}</td>
                    <td className="p-3 text-center">
                      <Badge variant="outline" className="text-xs border-zinc-600 text-zinc-400">
                        {site.region || '-'}
                      </Badge>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`font-mono text-sm ${site.officers_on_duty > 0 ? 'text-emerald-400' : 'text-zinc-500'}`}>
                        {site.officers_on_duty}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <span className="font-mono text-sm text-blue-400">{site.patrols_today}</span>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`font-mono text-sm ${site.incidents_today > 0 ? 'text-yellow-400' : 'text-zinc-500'}`}>
                        {site.incidents_today}
                      </span>
                    </td>
                  </tr>
                ))}

                {visibleSiteRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-zinc-500">
                      {siteRows.length === 0
                        ? 'No site activity data available.'
                        : 'No sites match these filters.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-start gap-x-6 gap-y-3 p-4 pr-24 border-t border-[#3f3f46]">
            <p className="text-xs text-zinc-400" aria-live="polite">
              {filteredSiteRows.length === 0
                ? '0 sites'
                : `${siteStart + 1}–${Math.min(siteStart + sitePageSize, filteredSiteRows.length)} of ${filteredSiteRows.length} sites`}
            </p>

            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={currentSitePage <= 1}
                onClick={() => setSitePage(currentSitePage - 1)}
              >
                Previous
              </Button>
              <span className="text-xs text-zinc-400">
                Page {currentSitePage} of {sitePageCount}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={currentSitePage >= sitePageCount}
                onClick={() => setSitePage(currentSitePage + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
